import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, Menu, AlertCircle, CheckCircle } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';

export default function Register() {
  const [searchParams] = useSearchParams();
  const betaCode = searchParams.get('ref') || '';

  const [status, setStatus] = useState(null); // null = loading, { open, seats_remaining, invalid_code }
  const [form, setForm] = useState({ name: '', workspaceName: '', email: '', password: '' });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) navigate('/menus', { replace: true });
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    api.get('/auth/beta-status', { params: { code: betaCode } })
      .then(res => setStatus(res.data))
      .catch(() => setStatus({ open: false, seats_remaining: 0 }));
  }, [betaCode]);

  const handleChange = (e) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) {
      setError('Please fill in all required fields');
      return;
    }
    if (form.password.length < 8 || !/\d/.test(form.password) || !/[a-zA-Z]/.test(form.password)) {
      setError('Password must be at least 8 characters and include a letter and a number');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/auth/register', {
        name: form.name,
        workspace_name: form.workspaceName || form.name,
        email: form.email,
        password: form.password,
        beta_code: betaCode,
      });
      // Log in immediately after registration
      const result = await login(form.email, form.password);
      if (result.success) navigate('/menus', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!status) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-zinc-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-zinc-900 border-zinc-800">
        <CardHeader className="space-y-2 text-center">
          <div className="flex justify-center mb-4">
            <div className="w-14 h-14 bg-emerald-500/10 rounded-xl flex items-center justify-center">
              <Menu className="w-8 h-8 text-emerald-500" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-zinc-100">
            LINE Rich Menu Builder
          </CardTitle>
          <CardDescription className="text-zinc-400">
            {status.open ? 'Create your free account — no credit card, no code required' : 'Public beta'}
          </CardDescription>
        </CardHeader>

        <CardContent>
          {status.invalid_code ? (
            <div className="text-center space-y-4">
              <Alert className="bg-zinc-800 border-zinc-700">
                <AlertCircle className="h-4 w-4 text-zinc-400" />
                <AlertDescription className="text-zinc-400">
                  This registration code is invalid or expired.
                </AlertDescription>
              </Alert>
              <p className="text-zinc-500 text-sm">
                You don't need a code to join the beta.{' '}
                <Link to="/register" className="text-emerald-500 hover:underline">Continue without a code</Link>
              </p>
            </div>
          ) : !status.open ? (
            <div className="text-center space-y-4">
              <Alert className="bg-amber-500/10 border-amber-500/20">
                <AlertCircle className="h-4 w-4 text-amber-400" />
                <AlertDescription className="text-amber-300">
                  The beta is full right now. We'll open more spots soon.
                </AlertDescription>
              </Alert>
              <p className="text-zinc-500 text-sm">
                <a href="https://linerichmenu.com#interest" className="text-emerald-500 hover:underline">
                  Leave your details
                </a>{' '}
                and we'll get back to you.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive" className="bg-red-500/10 border-red-500/20">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="name" className="text-zinc-300">Your Name <span className="text-red-400">*</span></Label>
                <Input
                  id="name" name="name" placeholder="Jane Smith"
                  value={form.name} onChange={handleChange}
                  className="bg-zinc-800 border-zinc-700 text-zinc-100 placeholder:text-zinc-500 focus:border-emerald-500"
                  disabled={submitting} autoFocus
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="workspaceName" className="text-zinc-300">
                  Company / Workspace Name <span className="text-zinc-500 text-xs">(optional)</span>
                </Label>
                <Input
                  id="workspaceName" name="workspaceName" placeholder="Acme Corp"
                  value={form.workspaceName} onChange={handleChange}
                  className="bg-zinc-800 border-zinc-700 text-zinc-100 placeholder:text-zinc-500 focus:border-emerald-500"
                  disabled={submitting}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-zinc-300">Email <span className="text-red-400">*</span></Label>
                <Input
                  id="email" name="email" type="email" placeholder="you@example.com"
                  value={form.email} onChange={handleChange}
                  className="bg-zinc-800 border-zinc-700 text-zinc-100 placeholder:text-zinc-500 focus:border-emerald-500"
                  disabled={submitting} autoComplete="email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-zinc-300">Password <span className="text-red-400">*</span></Label>
                <Input
                  id="password" name="password" type="password" placeholder="8+ characters, letters and numbers"
                  value={form.password} onChange={handleChange}
                  className="bg-zinc-800 border-zinc-700 text-zinc-100 placeholder:text-zinc-500 focus:border-emerald-500"
                  disabled={submitting} autoComplete="new-password"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-2.5"
                disabled={submitting}
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating account...</>
                ) : (
                  'Create Account'
                )}
              </Button>
              <p className="text-center text-xs text-zinc-500" data-testid="free-plan-note">
                Free plan includes 2 LINE channels per workspace. Upgrade anytime.
              </p>
            </form>
          )}

          <div className="mt-6 text-center">
            <p className="text-zinc-500 text-sm">
              Already have an account?{' '}
              <Link to="/login" className="text-emerald-500 hover:underline">Sign in</Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
